import React from 'react'
import styled, {css} from 'styled-components'

import ImageComponent from '@/components/image'
import type {Props as RichTextProps} from '@/components/rich-text'
import RichText from '@/components/rich-text'
import {Text} from '@/components/text'
import type {TextProps} from '@/components/text'
import {tokens, radii} from '@/theme'
import type {ImageSource} from '@/types/image'
import mq from '@/utils/media-query'

export interface Props {
    title: string
    titleVariant?: TextProps['$variant']
    body?: RichTextProps['content']
    image?: ImageSource
    bodyProps?: Partial<TextProps>
    as?: 'div' | 'li'
    direction?: 'vertical' | 'horizontal'
}

const MediaItem = ({
    title,
    titleVariant = 'paragraph-large',
    body,
    image,
    bodyProps,
    as = 'div',
    direction,
}: Props) => (
    <Item as={as} $numbered={!image} $direction={direction}>
        {!!image && <Image image={image} width={80} />}

        <Content>
            <Text $variant={titleVariant}>{title}</Text>

            <RichText $variant="paragraph-base" {...bodyProps} content={body} />
        </Content>
    </Item>
)

export default MediaItem

const breakpoint = 'md'

const Image = styled(ImageComponent)`
    object-fit: contain;
    width: 60px;
    height: 60px;

    ${mq.min(breakpoint)} {
        width: 80px;
        height: 80px;
    }
`

const Content = styled.div`
    display: grid;
    gap: ${tokens.spacing4};
`

interface ItemProps {
    $numbered?: boolean
    $direction?: Props['direction']
}

const numbered = css`
    &::before {
        display: flex;
        width: 40px;
        height: 40px;
        line-height: 1;
        content: counter(listCounter);
        border: ${({theme}) => theme.borders.secondary};
        border-radius: ${radii.circle};
        justify-content: center;
        align-items: center;
    }
`

const Item = styled.div<ItemProps>`
    display: grid;
    grid-template-columns: ${(props) => (props.$direction === 'vertical' ? '1fr' : 'auto 1fr')};
    gap: ${tokens.spacing8} ${tokens.spacing20};
    counter-increment: listCounter;

    ${(props) => props.$numbered && numbered}

    ${mq.min(breakpoint)} {
        gap: ${tokens.spacing10} ${tokens.spacing40};
    }
`
